import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { DeviceInfo } from "../services/vision/types";
import { DeviceInfoDisplay } from "../components/deviceInfo";
import { ErrorMessage } from "../components/ErrorMessage";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { CameraPreview } from "../components/CameraPreview";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { FiArrowLeft } from "react-icons/fi";
import { SavingOverlay } from "../components/SavingOverlay";

// Custom hooks
import { useCamera } from "../hooks/useCamera";
import { useImageProcessor } from "../hooks/useImageProcessor";
import { useRecordSaver } from "../hooks/useRecordSaver";
import { useFileUpload } from "../hooks/useFileUpload";

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.verae[500]};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 24px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

export const Scanner: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string>("");
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const deviceInfoRef = useRef<HTMLDivElement>(null);

  // Initialize custom hooks
  const {
    videoRef,
    canvasRef,
    cameraActive,
    isLoading: cameraLoading,
    startCamera,
    stopCamera,
    captureImage: captureCameraImage,
    setCameraActive,
  } = useCamera({
    onError: setError,
  });

  const {
    deviceInfo,
    isLoading: analysisLoading,
    analyzeImage,
    resizeImageForStorage,
    getBase64ImageSizeKB,
    setDeviceInfo,
  } = useImageProcessor({
    onError: setError,
  });

  const { isSaving, hashProgress, throughput, saveRecord } = useRecordSaver({
    onError: setError,
    onSuccess: resetScanner,
    resizeImageForStorage,
    getBase64ImageSizeKB,
  });

  const { fileInputRef, handleFileChange, handleFileUpload, isConverting } =
    useFileUpload({
      onImageCaptured: setCapturedImage,
      onResetCamera: () => {
        setCameraActive(false);
        stopCamera();
      },
      onAnalyzeImage: handleAnalyzeImage,
    });

  // Reset the scanner
  function resetScanner() {
    setCapturedImage(null);
    setDeviceInfo(null);
    setError("");
    setCameraActive(false);
    stopCamera();

    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  // Handle analyzing the captured image
  function handleAnalyzeImage() {
    if (capturedImage) {
      analyzeImage(capturedImage);
    }
  }

  // Handle capturing an image
  function handleCaptureImage() {
    const image = captureCameraImage();
    if (image) {
      setCapturedImage(image);
      setDeviceInfo(null);
      setError("");
    }
  }

  // Handle updating device info
  function handleUpdateDeviceInfo(updatedInfo: DeviceInfo) {
    setDeviceInfo(updatedInfo);
  }

  // Handle saving the record
  function handleSaveRecord() {
    if (capturedImage && deviceInfo) {
      saveRecord(capturedImage, deviceInfo);
    }
  }

  // Effect to scroll to device info when it becomes available
  useEffect(() => {
    if (deviceInfo && deviceInfoRef.current && !analysisLoading) {
      // Scroll to the device info section with a smooth animation
      deviceInfoRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [deviceInfo, analysisLoading]);

  const isLoading = cameraLoading || analysisLoading || isConverting;

  return (
    <Container>
      <BackButton onClick={() => navigate(-1)}>
        <FiArrowLeft size={18} />
        Back
      </BackButton>
      {/* <PageTitle
        icon={<FiCamera />}
        subtitle="Capture or upload an image of a device and we'll extract its information using AI."
      >
        Device Scanner
      </PageTitle> */}
      <Card>
        <CameraPreview
          cameraActive={cameraActive}
          capturedImage={capturedImage}
          videoRef={videoRef}
          canvasRef={canvasRef}
          onCapture={handleCaptureImage}
          onAnalyze={handleAnalyzeImage}
          onStartCamera={startCamera}
          onFileUpload={handleFileUpload}
          setCameraActive={setCameraActive}
          setCapturedImage={setCapturedImage}
          setDeviceInfo={setDeviceInfo}
          resetScanner={resetScanner}
          isConverting={isConverting}
        />

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
          accept="image/*,.heic,.heif"
        />
      </Card>

      {(error || deviceInfo) && (
        <>
          <Card ref={deviceInfoRef}>
            {error && (
              <ErrorMessage message={error} onDismiss={() => setError("")} />
            )}

            {deviceInfo && !isLoading && (
              <DeviceInfoDisplay
                deviceInfo={deviceInfo as DeviceInfo}
                onReset={resetScanner}
                onUpdate={handleUpdateDeviceInfo}
              />
            )}
          </Card>
          <Card>
            <Button
              onClick={handleSaveRecord}
              style={{ width: "100%" }}
              variant="primary"
              size="xl"
              disabled={
                isSaving ||
                !capturedImage ||
                !deviceInfo ||
                Object.keys(deviceInfo || {}).filter(
                  (key) => !["confidence", "aiEnhanced"].includes(key)
                ).length === 0 ||
                !deviceInfo.title
              }
            >
              Save Record
            </Button>
          </Card>
        </>
      )}

      {isLoading && (
        <LoadingOverlay
          message={
            isConverting
              ? "Converting image format..."
              : !cameraActive
              ? "Scanning..."
              : "Loading..."
          }
        />
      )}

      <SavingOverlay
        isVisible={isSaving}
        progress={hashProgress}
        throughput={throughput}
        message="Saving record..."
      />
    </Container>
  );
};
